<script>
  import { router } from '@inertiajs/svelte'
  
  let { user = {}, versions = [], sessions = [], recent_activity = [], audit_logs = [], flash = {} } = $props()
  let showRestrictModal = $state(false)
  let restrictReason = $state('')
  let isRestrictSubmitting = $state(false)
  
  function banUser() {
    if (confirm('정말 이 사용자를 정지하시겠습니까?')) {
      router.post(`/admin/users/${user.id}/ban`)
    }
  }
  
  function unbanUser() {
    if (confirm('정지를 해제하시겠습니까?')) {
      router.post(`/admin/users/${user.id}/unban`)
    }
  }
  
  function verifyUser() {
    if (confirm('이 사용자를 수동으로 인증하시겠습니까?')) {
      router.post(`/admin/users/${user.id}/verify`)
    }
  }

  function verifyCompany() {
    if (confirm('이 사용자의 회사 인증을 완료 처리하시겠습니까?')) {
      router.post(`/admin/users/${user.id}/verify_company`)
    }
  }

  function restrictCompanyAccess() {
    showRestrictModal = true
  }

  function closeRestrictModal() {
    showRestrictModal = false
    restrictReason = ''
    isRestrictSubmitting = false
  }

  function submitRestrictCompanyAccess() {
    isRestrictSubmitting = true

    router.post(
      `/admin/users/${user.id}/restrict_company_access`,
      { reason: restrictReason.trim() },
      {
        onFinish: () => {
          isRestrictSubmitting = false
        },
        onSuccess: () => {
          closeRestrictModal()
        }
      }
    )
  }

  function unrestrictCompanyAccess() {
    if (confirm('이 사용자의 회사 인증 접근 제한을 해제하시겠습니까?')) {
      router.post(`/admin/users/${user.id}/unrestrict_company_access`)
    }
  }

  function roleBadge(role) {
    switch (role) {
      case 'super_admin': return 'badge-error'
      case 'admin': return 'badge-primary'
      case 'moderator': return 'badge-info'
      default: return 'badge-neutral'
    }
  }

  function statusBadge(status) {
    switch (status) {
      case 'active': return 'badge-success'
      case 'pending': return 'badge-warning'
      case 'banned': return 'badge-error'
      case 'dormant': return 'badge-neutral'
      default: return 'badge-neutral'
    }
  }
</script>

<!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <span>/</span>
    <a href="/admin/users" class="hover:text-primary transition-colors">사용자</a>
    <span>/</span>
    <span class="text-content">{user.name}</span>
  </div>

  <main class="max-w-4xl">
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    
    <!-- User Info Card -->
    <div class="card card-bordered p-6 mb-6 shadow-md">
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          <div class="w-20 h-20 bg-primary-50 dark:bg-dark-tertiary rounded-full flex items-center justify-center text-primary dark:text-dark-text-high text-h1">
            {user.name?.charAt(0) || '?'}
          </div>
          <div>
            <h1 class="text-h1 text-content">{user.name}</h1>
            <p class="text-body2 text-content-secondary">{user.email}</p>
            {#if user.nickname}
              <p class="text-caption text-content-tertiary">@{user.nickname}</p>
            {/if}
          </div>
        </div>
        
        <div class="flex items-center gap-2">
          <a
            href={`/admin/users/${user.id}/edit`}
            class="btn btn-secondary btn-sm"
          >
            수정
          </a>
          {#if user.status === 'banned'}
            <button
              onclick={unbanUser}
              class="btn btn-primary btn-sm"
            >
              정지 해제
            </button>
          {:else}
            <button
              onclick={banUser}
              class="btn btn-danger btn-sm"
            >
              정지
            </button>
          {/if}
        </div>
      </div>
      
      <!-- Status Badges -->
      <div class="flex items-center gap-3 mb-6">
        <span class={`badge ${roleBadge(user.role)}`}>
          {user.role}
        </span>
        <span class={`badge ${statusBadge(user.status)}`}>
          {user.status}
        </span>
        {#if user.verified_at}
          <span class="badge badge-success">✓ 인증됨</span>
        {:else}
          <span class="badge badge-neutral">미인증</span>
          <button
            onclick={verifyUser}
            class="text-primary text-body2 font-medium hover:text-primary-dark transition-colors"
          >
            수동 인증
          </button>
        {/if}
        {#if user.company_verification_status === 'verified'}
          <span class="badge badge-success">회사 인증됨</span>
        {:else if user.company_verification_status === 'restricted'}
          <span class="badge badge-error">회사 인증 이용제한</span>
        {:else}
          <span class="badge badge-neutral">회사 미인증</span>
          <button
            onclick={verifyCompany}
            class="text-primary text-body2 font-medium hover:text-primary-dark transition-colors"
          >
            수동 인증
          </button>
        {/if}
      </div>
      
      <!-- Details Grid -->
      <div class="grid md:grid-cols-2 gap-6">
        <div class="space-y-4">
          <h3 class="text-label text-content-tertiary uppercase">기본 정보</h3>
          <dl class="space-y-2">
            <div class="flex justify-between">
              <dt class="text-content-tertiary">이메일</dt>
              <dd class="text-content">{user.email}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">가입일</dt>
              <dd class="text-content">{new Date(user.created_at).toLocaleString('ko-KR')}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">SSO 제공자</dt>
              <dd class="text-content">{user.provider || '이메일'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">초대 코드</dt>
              <dd class="text-content font-mono">{user.invite_code}</dd>
            </div>
          </dl>
        </div>
        
        <div class="space-y-4">
          <h3 class="text-label text-content-tertiary uppercase">소속 정보</h3>
          <dl class="space-y-2">
            <div class="flex justify-between">
              <dt class="text-content-tertiary">회사/기관</dt>
              <dd class="text-content">{user.organization_name || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">부서</dt>
              <dd class="text-content">{user.department || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">팀</dt>
              <dd class="text-content">{user.team || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">직책</dt>
              <dd class="text-content">{user.position || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">회사 이메일</dt>
              <dd class="text-content">{user.work_email || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">이메일 도메인</dt>
              <dd class="text-content font-mono">{user.company_domain || '-'}</dd>
            </div>
            <div class="flex justify-between">
              <dt class="text-content-tertiary">회사 인증 상태</dt>
              <dd class="text-content">{user.company_verification_status || 'unverified'}</dd>
            </div>
            {#if user.company_verified_at}
              <div class="flex justify-between">
                <dt class="text-content-tertiary">회사 인증 일시</dt>
                <dd class="text-content">{new Date(user.company_verified_at).toLocaleString('ko-KR')}</dd>
              </div>
            {/if}
            {#if user.company_restricted_at}
              <div class="flex justify-between">
                <dt class="text-content-tertiary">회사 제한 일시</dt>
                <dd class="text-error">{new Date(user.company_restricted_at).toLocaleString('ko-KR')}</dd>
              </div>
            {/if}
            {#if user.manual_company_access_block_reason}
              <div class="flex justify-between gap-4">
                <dt class="text-content-tertiary shrink-0">제한 사유</dt>
                <dd class="text-content text-right">{user.manual_company_access_block_reason}</dd>
              </div>
            {/if}
          </dl>
        </div>
      </div>

      <div class="flex items-center gap-3 mt-6 pt-6 border-t border-default">
        {#if user.company_verification_status !== 'verified'}
          <button onclick={verifyCompany} class="btn btn-primary btn-sm">회사 인증 처리</button>
        {/if}
        {#if user.company_access_blocked}
          <button onclick={unrestrictCompanyAccess} class="btn btn-secondary btn-sm">인증 제한 해제</button>
        {:else}
          <button onclick={restrictCompanyAccess} class="btn btn-danger btn-sm">회사 인증 제한</button>
        {/if}
      </div>
    </div>
    
    <!-- Sessions -->
    <div class="card card-bordered overflow-hidden mb-6 shadow-md">
      <div class="px-6 py-4 border-b border-default">
        <h2 class="text-title text-content">최근 세션</h2>
      </div>
      
      {#if sessions.length > 0}
        <table class="w-full">
          <thead class="bg-surface-tertiary">
            <tr>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">IP 주소</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">User Agent</th>
              <th class="px-6 py-3 text-left text-caption font-semibold text-content-tertiary uppercase">접속 시간</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-dark-border">
            {#each sessions as session}
              <tr>
                <td class="px-6 py-3 text-content font-mono">{session.ip_address}</td>
                <td class="px-6 py-3 text-content-secondary text-body2 truncate max-w-xs">{session.user_agent}</td>
                <td class="px-6 py-3 text-content-secondary text-body2">{new Date(session.created_at).toLocaleString('ko-KR')}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {:else}
        <div class="px-6 py-8 text-center text-content-tertiary">세션 기록이 없습니다</div>
      {/if}
    </div>
    
    <!-- Recent Activity -->
    {#if recent_activity.length > 0}
      <div class="card card-bordered overflow-hidden mb-6 shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">최근 활동</h2>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each recent_activity as activity}
            <div class="px-6 py-3 flex items-center gap-3">
              <div class="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm
                {activity.type === 'post' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600' :
                 activity.type === 'comment' ? 'bg-green-50 dark:bg-green-900/20 text-green-600' :
                 activity.type === 'report_filed' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600' :
                 'bg-red-50 dark:bg-red-900/20 text-red-600'}">
                {activity.type === 'post' ? '📝' : activity.type === 'comment' ? '💬' : activity.type === 'report_filed' ? '🚨' : '⚠️'}
              </div>
              <div class="flex-1 min-w-0">
                {#if activity.type === 'post'}
                  <a href={`/admin/posts/${activity.id}`} class="text-body2 text-primary hover:text-primary-dark truncate block">{activity.title}</a>
                  <span class="text-caption text-content-tertiary">게시글 · {activity.status}</span>
                {:else if activity.type === 'comment'}
                  <span class="text-body2 text-content truncate block">{activity.content}</span>
                  <span class="text-caption text-content-tertiary">댓글 · {activity.post_title || ''}</span>
                {:else if activity.type === 'report_filed'}
                  <a href={`/admin/reports/${activity.id}`} class="text-body2 text-orange-600 hover:text-orange-700 truncate block">{activity.reason} ({activity.target_type})</a>
                  <span class="text-caption text-content-tertiary">신고 접수 · {activity.status}</span>
                {:else}
                  <a href={`/admin/reports/${activity.id}`} class="text-body2 text-red-600 hover:text-red-700 truncate block">피신고: {activity.reason}</a>
                  <span class="text-caption text-content-tertiary">신고 수신 · {activity.status}</span>
                {/if}
              </div>
              <span class="text-caption text-gray-400 dark:text-dark-text-disabled whitespace-nowrap">
                {new Date(activity.created_at).toLocaleDateString('ko-KR')}
              </span>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Admin Audit Logs for this User -->
    {#if audit_logs.length > 0}
      <div class="card card-bordered overflow-hidden mb-6 shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">관리자 처리 이력</h2>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each audit_logs as log}
            <div class="px-6 py-3 flex items-center justify-between">
              <div>
                <span class="text-body2 font-medium text-content">{log.action}</span>
                <span class="text-caption text-content-tertiary ml-2">by {log.admin_name}</span>
              </div>
              <span class="text-caption text-gray-400 dark:text-dark-text-disabled">
                {new Date(log.created_at).toLocaleString('ko-KR')}
              </span>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <!-- Version History -->
    {#if versions.length > 0}
      <div class="card card-bordered overflow-hidden shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">변경 이력</h2>
        </div>
        
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each versions as version}
            <div class="px-6 py-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-content font-medium">{version.event}</span>
                <span class="text-caption text-content-tertiary">{new Date(version.created_at).toLocaleString('ko-KR')}</span>
              </div>
              {#if version.whodunnit}
                <div class="text-caption text-content-tertiary">by User #{version.whodunnit}</div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </main>

{#if showRestrictModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
    <div class="w-full max-w-lg card card-bordered bg-surface p-6 shadow-xl">
      <div class="mb-4">
        <h3 class="text-h2 text-content">회사 인증 제한</h3>
        <p class="text-body2 text-content-secondary mt-1">
          제한 사유를 남기면 관리자 이력 확인 시 도움이 됩니다. (선택)
        </p>
      </div>

      <div class="mb-6">
        <label for="restrict_reason" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">
          제한 사유
        </label>
        <textarea
          id="restrict_reason"
          bind:value={restrictReason}
          rows="4"
          maxlength="300"
          class="input w-full"
          placeholder="예: 기관 정책 위반으로 도메인 사용자 이용 제한"
        ></textarea>
        <p class="mt-1 text-caption text-gray-400 dark:text-dark-text-disabled text-right">
          {restrictReason.length}/300
        </p>
      </div>

      <div class="flex items-center justify-end gap-2">
        <button type="button" class="btn btn-secondary btn-sm" onclick={closeRestrictModal} disabled={isRestrictSubmitting}>
          취소
        </button>
        <button type="button" class="btn btn-danger btn-sm" onclick={submitRestrictCompanyAccess} disabled={isRestrictSubmitting}>
          {isRestrictSubmitting ? '처리 중...' : '제한 적용'}
        </button>
      </div>
    </div>
  </div>
{/if}
